import { useState, type FormEvent } from 'react';
import { useSiteContent } from '../content/ContentContext';

const initialState = {
  name: '',
  phone: '',
  email: '',
  address: '',
  service: '',
  message: '',
};

export default function ContactForm() {
  const { content, serviceOptions } = useSiteContent();
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitState('idle');

    try {
      const response = await fetch(content.site.formspree, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Form submission failed');
      }

      setSubmitState('success');
      setFormData(initialState);
    } catch {
      setSubmitState('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
      <div className="grid gap-5 md:grid-cols-2">
        <label className="text-sm font-semibold text-slate-700">
          Name
          <input
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-[#33b300]"
          />
        </label>
        <label className="text-sm font-semibold text-slate-700">
          Phone
          <input
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-[#33b300]"
          />
        </label>
        <label className="text-sm font-semibold text-slate-700">
          Email
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-[#33b300]"
          />
        </label>
        <label className="text-sm font-semibold text-slate-700">
          Property Address
          <input
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-[#33b300]"
          />
        </label>
      </div>
      <label className="mt-5 block text-sm font-semibold text-slate-700">
        Service Needed
        <select
          required
          value={formData.service}
          onChange={(e) => setFormData({ ...formData, service: e.target.value })}
          className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-[#33b300]"
        >
          <option value="">Select a service</option>
          {serviceOptions.map((service) => (
            <option key={service} value={service}>
              {service}
            </option>
          ))}
          <option value="Other">Other</option>
        </select>
      </label>
      <label className="mt-5 block text-sm font-semibold text-slate-700">
        Message
        <textarea
          rows={5}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-[#33b300]"
          placeholder="Tell us what you need done."
        />
      </label>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center justify-center rounded-full bg-[#0d4f12] px-6 py-3 font-bold text-white transition hover:bg-[#0a3c0e] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? 'Sending...' : 'Send Request'}
        </button>
        {submitState === 'success' ? <p className="text-sm font-semibold text-[#0d4f12]">Thank you for reaching out. We will get back to you as soon as possible.</p> : null}
        {submitState === 'error' ? <p className="text-sm font-semibold text-red-600">Something went wrong. Please call or email us directly.</p> : null}
      </div>
    </form>
  );
}
